import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserShopMemberships } from '../../hooks/useShops';
import { useShopStore } from '../../store/slices/shopSlice';
import { useSettingsStore } from '../../store/settingsStore'; 
import { FaStore, FaUserTie, FaUserCog, FaUser, FaSpinner, FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext'; 
import { gsap } from 'gsap';

const translations = {
    en: {
        authenticating: "Checking your account...",
        accessDenied: "Access Denied",
        loginRequired: "You must be logged in to view your businesses.",
        returnBtn: "Go to Login",
        title: "Your Businesses",
        subtitle: "Select a business to open its dashboard",
        loadingMemberships: "Loading your businesses...",
        errorHeading: "An error occurred:",
        errorFallback: "Could not connect to the network.",
        noShopsTitle: "No Businesses Found",
        noShopsDesc: "Your account is not linked to any business yet. Please ask the business owner to add you.",
        welcome: "Welcome to"
    },
    fr: {
        authenticating: "Vérification de votre compte...",
        accessDenied: "Accès refusé",
        loginRequired: "Vous devez être connecté pour voir vos entreprises.",
        returnBtn: "Se connecter",
        title: "Vos Entreprises",
        subtitle: "Sélectionnez une entreprise",
        loadingMemberships: "Chargement de vos entreprises...",
        errorHeading: "Une erreur est survenue :",
        errorFallback: "Impossible de se connecter au réseau.",
        noShopsTitle: "Aucune entreprise trouvée",
        noShopsDesc: "Votre compte n'est lié à aucune entreprise pour le moment. Veuillez demander au propriétaire de vous ajouter.",
        welcome: "Bienvenue chez"
    }
};

const LoginToShop = () => {
    const navigate = useNavigate();
    const { user, loading: isAuthLoading } = useAuth();
    const userId = user?.id; 
    const setCurrentShop = useShopStore(state => state.setCurrentShop);
    
    const lang = useSettingsStore(state => state.lang) || 'en';
    const t = translations[lang] || translations.en;

    const pageRef = useRef(null);

    const { 
        data: memberships, 
        isLoading, 
        isError, 
        error 
    } = useUserShopMemberships(userId);

    useEffect(() => {
        if (!isAuthLoading && userId && pageRef.current) {
            const ctx = gsap.context(() => {
                gsap.fromTo('.anim-card', 
                    { opacity: 0, y: 25 },
                    { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }
                );
                
                gsap.fromTo('.anim-item', 
                    { opacity: 0, y: 15 },
                    { opacity: 1, y: 0, duration: 0.4, stagger: 0.08, ease: 'power2.out', delay: 0.15 }
                );
            }, pageRef.current);

            return () => ctx.revert();
        }
    }, [isAuthLoading, userId, isLoading]);

    const getRoleIcon = (role) => {
        switch (role) {
            case 'owner':
                return <FaUserTie size={13} color='red' className="text-rose-600" />; 
            case 'manager':
                return <FaUserCog size={13} color='orange' className="text-amber-600" />; 
            case 'staff':
                return <FaUser size={13} color='blue' className="text-blue-600" />; 
            default:
                return <FaStore size={13} color='grey' className="text-slate-600" />; 
        }
    };

    const handleShopLogin = (membership) => {
        if (pageRef.current) {
            gsap.to('.anim-card', {
                opacity: 0,
                y: -15,
                duration: 0.3,
                ease: 'power2.in',
                onComplete: () => {
                    setCurrentShop(membership.shop); 
                    toast.success(`${t.welcome} ${membership.shop.name}! (${membership.role.toUpperCase()})`);
                    navigate('/shop/dashboard');
                }
            });
        } else {
            setCurrentShop(membership.shop); 
            navigate('/shop/dashboard');
        }
    };

    const handleBackNavigation = () => {
        if (pageRef.current) {
            gsap.to('.anim-card', {
                opacity: 0,
                y: 15,
                duration: 0.25,
                ease: 'power2.in',
                onComplete: () => navigate(-1)
            });
        } else {
            navigate(-1);
        }
    };

    const safeParse = (field) => {
        if (!field) return null;
        if (typeof field === 'object') return field;
        try { return JSON.parse(field); } catch { return null; }
    };

    if (isAuthLoading) {
        return (
            <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center justify-center p-6">
                <div className="flex flex-col items-center gap-3 text-center">
                    <FaSpinner size={32} color='black' className="animate-spin text-slate-900" />
                    <p className="text-sm font-medium text-slate-500 tracking-wide">{t.authenticating}</p>
                </div>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 shadow-sm text-center">
                    <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 0v2m0-2h2m-2 0H10m3-13a4 4 0 11-8 0 4 4 0 018 0zM15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{t.accessDenied}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">{t.loginRequired}</p>
                    <button 
                        onClick={() => navigate('/sign-in')}
                        className="inline-flex items-center justify-center px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors"
                    >
                        {t.returnBtn}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div ref={pageRef} className="min-h-screen w-full bg-slate-50 flex items-center justify-center md:p-8 relative">
            <div className="anim-card opacity-0 w-full max-w-xl p-6 md:p-8">
                <div className="top-6 left-6 z-50">
                    <button
                        onClick={handleBackNavigation}
                        className="flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-slate-200 shadow-md text-slate-800 hover:bg-slate-50 hover:text-blue-600 transition-all active:scale-95"
                        aria-label="Go back"
                    >
                        <FaArrowLeft size={16} color='black' />
                    </button>
                </div>

                {/* Header Row */}
                <div className="mt-6 pb-4 border-b text-center">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight text-slate-900">{t.title}</h2>
                        <p className="text-sm font-medium text-slate-400 mt-0.5">{t.subtitle}</p>
                    </div>
                </div>
                <div className="mt-2 mb-5 pb-5 border-b items-center border-slate-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <button
                        className='bg-indigo-600 min-h-12 font-semibold px-4 border-2 rounded-full text-white'
                        onClick={()=> navigate('/shops/onboarding')}
                    >
                        {lang === "en" ? ("Create another Business") : ("Créer une Entreprise")}
                    </button>
                </div>

                {isLoading && (
                    <div className="text-center py-12 flex flex-col items-center justify-center gap-3">
                        <FaSpinner size={24} color='black' className="animate-spin text-slate-400" />
                        <p className="text-sm text-slate-400 font-medium">{t.loadingMemberships}</p>
                    </div>
                )}
                
                {isError && (
                    <div className="p-4 bg-rose-50 border border-rose-100 text-rose-700 text-sm rounded-xl mb-6">
                        <p className="font-semibold">{t.errorHeading}</p>
                        <p className="text-rose-600/90 mt-0.5">{error?.message || t.errorFallback}</p>
                    </div>
                )}

                {memberships && memberships.length > 0 ? (
                    <div className="space-y-4">
                        {memberships.map((membership) => {
                            const shop = membership.shop;
                            const shopTypeParsed = safeParse(shop.shop_type);
                            const locationParsed = safeParse(shop.location);
                            
                            const businessCategory = shopTypeParsed ? (shopTypeParsed[lang] || shopTypeParsed['en']) : '';
                            const businessStyle = shopTypeParsed?.business_style || 'Products';

                            const displayAddress = locationParsed 
                                ? `${locationParsed.address || ''}${locationParsed.quarter ? `, ${locationParsed.quarter}` : ''}`
                                : '';

                            return (
                                <div 
                                    key={shop.id} 
                                    onClick={() => handleShopLogin(membership)}
                                    style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                                    className="anim-item opacity-0 p-5 bg-white border border-slate-200 hover:border-slate-400 rounded-xl cursor-pointer hover:shadow-lg hover:shadow-slate-100/80 transition-all duration-200 group flex flex-col gap-4 select-none touch-manipulation"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-center min-w-0 gap-4">
                                            <img 
                                                src={shop.shop_logo_url || 'https://via.placeholder.com/60'} 
                                                alt={`${shop.name} logo`}
                                                className="w-14 h-14 object-cover rounded-xl border border-slate-200 shadow-inner group-hover:border-slate-300 transition-colors flex-shrink-0"
                                            />
                                            <div className="min-w-0">
                                                <h3 className="font-bold text-lg text-slate-800 truncate group-hover:text-slate-900 transition-colors leading-snug">
                                                    {shop.name}
                                                </h3>
                                                {businessCategory && (
                                                    <p className="text-sm font-semibold text-slate-400 mt-0.5 tracking-wide">
                                                        {businessCategory}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-bold uppercase tracking-wider rounded-lg border flex-shrink-0 ${
                                            membership.role === 'owner' ? 'bg-rose-50/70 text-rose-700 border-rose-100' :
                                            membership.role === 'manager' ? 'bg-amber-50/70 text-amber-700 border-amber-100' :
                                            'bg-blue-50/70 text-blue-700 border-blue-100'
                                        }`}>
                                            {getRoleIcon(membership.role)}
                                            <span className="text-[10px] tracking-wide">{membership.role}</span>
                                        </span>
                                    </div>

                                    <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
                                        {displayAddress ? (
                                            <div className="flex items-center gap-1.5 max-w-[70%] truncate text-slate-400 font-medium">
                                                <FaMapMarkerAlt className="text-slate-300 flex-shrink-0" size={11} color='black'/>
                                                <span className="truncate">{displayAddress}</span>
                                            </div>
                                        ) : (
                                            <div />
                                        )}
                                        
                                        <div className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-black tracking-wider text-[9px] uppercase">
                                            {businessStyle}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    !isLoading && (
                        <div className="p-8 text-center bg-slate-50 border border-slate-200/60 rounded-xl">
                            <div className="w-10 h-10 rounded-full bg-slate-200/60 flex items-center justify-center mx-auto mb-3">
                                <FaStore size={16} color='black' className="text-slate-500" />
                            </div>
                            <p className="text-sm font-semibold text-slate-700">{t.noShopsTitle}</p>
                            <p className="text-sm text-slate-400 mt-1 max-w-xs mx-auto leading-relaxed">
                                {t.noShopsDesc}
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default LoginToShop;